import WorkflowList from './WorkflowList';
import { useApplicationStore } from "./ApplicationStore";
import { useWorkflowFavorite } from "./useApplicationQueries";
import { Application } from "./ApplicationInterface";
import EmptyStateCard from "../common/EmptyStateCard";
import { useIPRestrictionStore } from "../dealer-admin/ip-access/IPRestrictionStore";
import BannerContainer from "../campaign/BannerContainer";
import OutageNotificationContainer from '../notification/OutageNotificationContainer';
import { useHistory } from "react-router-dom";
import toTestId from "../utils/toTestId";
import { useAnalytics } from '../third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';
import useAudit from '../../hooks/useAudit';
import { useTranslation } from "react-i18next";
import {Heading} from 'cdk-radial';
import AhaWidget from '../third-party-integration/aha/AhaWidget';
import {AhaPilotWidget} from '../third-party-integration/aha/AhaPilotWidget';
import { useState } from 'react';
import { useUpdateDefaultWorkflow } from '../startup-app/useUpdateDefaultWorkflow';
import { useToastStore } from '../components/Toast/useToastStore';
import userProfileStore from '../core/UserProfileStore';
import SetStartupAppDialog from '../startup-app/SetStartupAppDialog';


export function FavoriteWorkflowContainer() {
  const { applications } = useApplicationStore();
  const filteredData = applications.map((app: any) => ({
    ...app,
    workflows: app?.workflows?.filter(
      (workflow: any) => workflow.isFavorite === true
    ),
  }));
  const check = filteredData?.some((app: any)=> app?.workflows?.length > 0)
  return (
    <>
      {check ? (
        <WflowContainer applications={filteredData}></WflowContainer>
      ) : (
        <EmptyStateCard isApplication={false} isUserCard={false} />
      )}
    </>
  );
}

function WorkflowContainer() {
  const { applications } = useApplicationStore();
  const { t } = useTranslation();
  return (
    <div style={{maxWidth:'1664px',marginBottom:'20px'}}>
      <OutageNotificationContainer/>
      <BannerContainer/>
      <div style={{display: 'flex','justifyContent': 'space-between','flexDirection': 'row' , 'marginBottom':'10px', 'marginTop':'10px'}}>
          <Heading type="subtitle-1" level={6}>
            {t("myUnifyTab.workflows")}
        </Heading>
      <div style={{display: 'flex', gap: '10px'}}>
        <div style={{display: 'flex'}}>
          <AhaWidget></AhaWidget>
        </div>
        <div style={{display: 'flex'}}>
            <AhaPilotWidget></AhaPilotWidget>
        </div>
      </div>
    </div>
    
    <WflowContainer applications={applications}></WflowContainer>
    </div>
  );
}

function WflowContainer(props: { applications: Application[] }) {
  const { applications } = props;
    const { captureEvent} = useAudit();
    const { trackEvent } = useAnalytics();
  const history = useHistory();
  const { mutation } = useWorkflowFavorite();
  const ipRestriction = useIPRestrictionStore((state:any) => state.ipRestriction);
  const { t } = useTranslation();

  const { mutate: setDefaultWorkflowMutation } = useUpdateDefaultWorkflow();
  const { openToast } = useToastStore();
  const { unifyUserProfile } = userProfileStore();
  const { applications: activeApplications } = useApplicationStore();
  const [startupDialogState, setStartupDialogState] = useState<{
    isOpen: boolean;
    workflowId: string;
    workflowName: string;
    isDefault: boolean;
  }>({ isOpen: false, workflowId: '', workflowName: '', isDefault: false });

  const isWorkflowDefault = (workflowId: string): boolean => {
    if (!unifyUserProfile?.workflows || !Array.isArray(unifyUserProfile.workflows)) return false;
    return unifyUserProfile.workflows.some(
      (wf: any) => wf.workflowId === workflowId && wf.isDefaultWorkflow === true
    );
  };

  const currentDefaultWorkflow = unifyUserProfile?.workflows?.find(
    (wf: any) =>
      (wf.isDefaultWorkflow === true) &&
      (wf.workflowId) !== startupDialogState.workflowId
  );

  const currentDefaultApp = unifyUserProfile?.apps?.find(
    (app: any) => app.isDefaultApp === true
  );

  const currentDefaultName = (() => {
    if (currentDefaultApp) {
      const appId = currentDefaultApp.appId;
      return activeApplications?.find((app: any) => app.id === appId)?.name || '';
    }
    if (currentDefaultWorkflow) {
      const wfId = currentDefaultWorkflow.workflowId;
      // find workflow name from applications
      for (const app of activeApplications || []) {
        const wf = app.workflows?.find((w: any) => w.workflowId === wfId);
        if (wf) return wf.name;
      }
    }
    return '';
  })();

  const getDialogScenario = (): 'set' | 'remove' | 'update' => {
    if (startupDialogState.isDefault) return 'remove';
    if (currentDefaultApp || currentDefaultWorkflow) return 'update';
    return 'set';
  };

  const onSetDefaultWorkflow = (workflowId: string, workflowName: string, isDefault: boolean) => {
    setStartupDialogState({ isOpen: true, workflowId, workflowName, isDefault });
  };

  const confirmDefaultWorkflow = () => {
    const settingAsDefault = !startupDialogState.isDefault;
    setDefaultWorkflowMutation(
      { id: startupDialogState.workflowId, isDefault: settingAsDefault },
      {
        onSuccess: () => {
          trackEvent(AMPLITUDE_EVENTS.SET_STARTUP, {
            'Workflow': startupDialogState.workflowName,
            'Action': settingAsDefault ? 'Set' : 'Remove',
          });
          if (settingAsDefault) {
            openToast('startup-toast', 'Success', t('baseCard.startupApp.toastSet', { appName: startupDialogState.workflowName }));
          } else {
            openToast('startup-toast', 'Success', t('baseCard.startupApp.toastRemove', { appName: startupDialogState.workflowName }));
          }
        },
        onError: () => {
          openToast('startup-toast', 'Error', t('baseCard.startupApp.toastError'));
        },
      }
    );
    setStartupDialogState({ isOpen: false, workflowId: '', workflowName: '', isDefault: false });
  };


  const onCardClick = (appId:string,item: any) => {
      captureEvent({action:"Opened", type:"Workflow", name :item.name});
      trackEvent(AMPLITUDE_EVENTS.OPEN_WORKFLOW, {
        Workflow: item.name,
      });
      history.push(`/unify/wview/${appId}/${item.id}/N`);
  };

  const onCdkUniversityClick = (appId:string,workflowId:string,name:string) => {
    trackEvent(AMPLITUDE_EVENTS.OPEN_APPLICATION, {
        Application: name,
      });
      history.push(`/unify/wview/${appId}/${workflowId}/Y`);
  };

  const onOpenInNewTabClick = (appId:string, id:string,name:string, url:string) =>{
    const urlParams = new URLSearchParams(url.split('?')[1]);
    const launchType = urlParams.get('launchtype');
    if(launchType === 'tab') {
      const thisEl = document.getElementById(`wf-card-${toTestId(name)}`);
      thisEl?.click();
      return;
    }
    window.open(`${window.location.protocol}//${ window.location.host }/unify/wview/${appId}/${id}/N`, '_blank');
  }

  const onFavoriteChange = (appId:string,id:string,name:string,isFavorite:boolean) => {
    const fav = { appId , id, isFavorite}
    isFavorite && trackEvent(AMPLITUDE_EVENTS.OPEN_WORKFLOW, {
        'Favorite Workflow': name,
    });
    mutation.mutate(fav);
  };

return (
  <>
 
    <WorkflowList 
      applications={applications} 
      onClick={onCardClick}
      onCdkUniversityClick={onCdkUniversityClick}
      onFavoriteChange={onFavoriteChange}
      onOpenInNewTabClick={onOpenInNewTabClick}
      restrictions={ipRestriction}
      onSetDefaultWorkflow={onSetDefaultWorkflow}
      isWorkflowDefault={isWorkflowDefault}
      />
      <SetStartupAppDialog
      isOpen={startupDialogState.isOpen}
      appName={startupDialogState.workflowName}
      scenario={getDialogScenario()}
      previousDefaultAppName={currentDefaultName}
      onConfirm={confirmDefaultWorkflow}
      onDismiss={() => setStartupDialogState({ isOpen: false, workflowId: '', workflowName: '', isDefault: false })}
    />
  </>
);
}

export default WorkflowContainer;